import { useState } from "owa-react";
import { Button, Divider, mergeClasses, Tooltip } from "@fluentui/react-components";
import { bundleIcon, SearchFilled, SearchRegular } from "@fluentui/react-icons";
import { useCurrentRoute } from "accelerator-common/lib/AccNavTabList";
import { AccTabList } from "accelerator-common/lib/AccTabList";
import { HybridspaceSectionNavigation, useSectionNavigation, } from "hybridspace-common/lib/commonHooks/useSectionNavigation";
import { ADMIN_ROOT, SPACE_ANALYTICS, SPACE_MANAGEMENT } from "hybridspace-common/lib/constants";
import { useRoleContext, UserRoles } from "hybridspace-common/lib/context";
import { isSpaceManagementPage } from "hybridspace-common/lib/utils";
import { isSpaceAnalyticAdminTabEnabled, useIsSpaceManagementAdminTabEnabled, } from "hybridspace-common/lib/utils/adminUtils";
import { default as useHybridspaceAdminLinks } from "hybridspace-common/lib/utils/useHybridspaceAdminLinks";
import { useWindowBreakpoints } from "hybridspace-common/lib/utils/useWindowDimensions";
import { PEOPLE, useHybridspaceHomeLinks } from "hybridspace-home";
import { isPlacesAnalyticsHomePage } from "hybridspace-performance-datapoints";
import { logPlacesEvent } from "hybridspace-telemetry";
import { getHostHub } from "owa-config";
import { isGlobalFeatureEnabled, isAccountFeatureEnabled } from "owa-feature-flags";
import loc from "owa-localize";
import { observer } from "owa-mobx-react";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import { type UTExploreCardType, type UTExploreInterationType, type UTPageType } from "@outlook/unified_telemetry";
import { useListUserFeatureAccessSelector } from "places-analytics-app-store";
import { PlacesSearchBar } from "places-explore-search";
import { placesAnalyticsAppLoadAction } from "places-fwk-actions";
import { isPlacesExploreEnabled, isPlacesSearchEnabled } from "places-settings";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HybridspaceLogo from "../HybridspaceLogo";
import HybridspaceRoleSwitcher from "../HybridspaceRoleSwitcher/HybridspaceRoleSwitcher";
import PlacesMoreDropdown from "../PlacesMoreDropdown/PlacesMoreDropdown";
import { searchButtonAriaLabel } from "./HybridspaceNavHeader.locstring.json";
import { divider, meControlPadding, meControlWhiteBackground, metaOSPadding, navHeader, navHeaderMetaOS, navHeaderUpNextRefresh, placesSearchBar, placesSearchBarActive, searchButton, settingsArea, } from "./HybridspaceNavHeader.scss";
import HybridspaceSuiteHeader from "./HybridspaceSuiteHeader";
import SmallScreenNav from "./SmallScreenNav";
import { useRoleSwitcherLoadE2E } from "./useRoleSwitcherLoadE2E";

import type { SearchSuggestion } from "accelerator-search-bar";
const SearchIcon = bundleIcon(SearchFilled, SearchRegular);
const searchIcon = <SearchIcon />;

export interface HybridspaceNavHeaderProps {
    showCompactAppHeader: boolean;
}
const defaultExport: React.FunctionComponent<HybridspaceNavHeaderProps> = observer(function HybridspaceNavHeader({ showCompactAppHeader }: HybridspaceNavHeaderProps) {
    const hostHub = getHostHub();
    const isStandaloneApp = hostHub === undefined;
    
    const navigate = useNavigate();
    const location = useLocation().pathname;
    
    const mailboxInfo = getModuleContextMailboxInfo();
    const explorePage = isPlacesExploreEnabled(mailboxInfo);
    const searchEnabled = isPlacesSearchEnabled(mailboxInfo);
    
    const { featureAccess, loading } = useListUserFeatureAccessSelector();
    const isSpaceAnalyticAdminEnabled = isSpaceAnalyticAdminTabEnabled(featureAccess);
    const { hasPermission: isSpaceManagementAdminEnabled, isChecking, checkFailed, } = useIsSpaceManagementAdminTabEnabled();
    const isAdminEnabled = isSpaceAnalyticAdminEnabled || isSpaceManagementAdminEnabled;
    
    const isAnalyticsHomePage = isPlacesAnalyticsHomePage(window?.location?.pathname || "");
    const isSpaceManagementAdminPage = isSpaceManagementPage(window?.location?.pathname || "");
    
    const homeLinks = useHybridspaceHomeLinks();
    const adminLinks = useHybridspaceAdminLinks({
        // If user is on the Management or Analytics page through url path, the tab should be visible
        isSpaceManagementEnabled: isSpaceManagementAdminEnabled || isSpaceManagementAdminPage,
        isSpaceAnalyticEnabled: isSpaceAnalyticAdminEnabled || isAnalyticsHomePage,
    });
    const roleSwitcherDisabled = isAccountFeatureEnabled("msplaces-disableRoleSwitcher", mailboxInfo);
    
    const links = React.useMemo(() => [...homeLinks, ...adminLinks], [homeLinks, adminLinks]);
    
    const defaultRoute = useCurrentRoute(links);
    
    /* eslint-disable-next-line owa-custom-rules/prefer-react-state-without-arrays-or-objects -- (https://aka.ms/OWALintWiki)
     * Please remove the array or object from React.useState() or leave a justification in case is not possible to do so.
     *	> It is preferable not to use arrays or objects as react state, destruct the state into primitive data types or use satchel instead. */
    const [selectedTab, setSelectedTab] = useState(defaultRoute);
    
    React.useEffect(() => {
        if (!loading && isAnalyticsHomePage && !isSpaceAnalyticAdminEnabled) {
            // if the user doesn't have access to admin analytics, stop Perf Marker as it is a valid scenario.
            placesAnalyticsAppLoadAction({
                scenario: "NonAdminRBACSettingsFetched",
            });
        }
    }, [loading, isSpaceAnalyticAdminEnabled, isAnalyticsHomePage]);
    
    const { userRole, setUserRole } = useRoleContext();
    
    React.useEffect(() => {
        if (location.includes(ADMIN_ROOT)) {
            setUserRole(UserRoles.Admin);
        }
        else {
            setUserRole(UserRoles.Employee);
        }
    }, [location, userRole, setUserRole]);
    
    const [isActiveSearchBar, setIsActiveSearchBar] = useState(false);
    
    const { isScreenSmallDown, isScreenMediumDown, isScreenLargeDown, isScreenXLargeUp } = useWindowBreakpoints();
    
    const showTabList = !isScreenSmallDown;
    const searchingOnSmallScreen = isActiveSearchBar && isScreenSmallDown;
    const searchingOnMediumScreen = isActiveSearchBar && isScreenLargeDown;
    
    const routeToPageMap = React.useMemo(() => new Map([
        //TODO: add workgroups path resolving here
        [`/${PEOPLE}`, "People"],
        [`/${ADMIN_ROOT}`, "AdminDashboard"],
        [`/${ADMIN_ROOT}/${SPACE_ANALYTICS}`, "SpaceAnalytics"],
        [`/${ADMIN_ROOT}/${SPACE_MANAGEMENT}`, "SpaceManagement"],
        ["/", explorePage ? "Explore" : "People"],
    ]), [explorePage]);
    
    const handleSelectedKeyChange = React.useCallback((newSelectedKey: string) => {
        const sourcePage = routeToPageMap.get(selectedTab);
        const destPage = routeToPageMap.get(newSelectedKey);
        setSelectedTab(newSelectedKey);
        navigate(newSelectedKey);
        
        // Check that the source and destination pages are both defined and different before logging the event
        if (sourcePage && destPage && sourcePage != destPage) {
            logPlacesEvent({
                eventName: "PlacesNavigationClick",
                data: {
                    SourcePage: sourcePage,
                    DestinationPage: destPage,
                },
            });
        }
    }, [routeToPageMap, selectedTab, navigate]);
    
    const onSearchButtonClick = React.useCallback(() => {
        logPlacesEvent({
            eventName: "PlacesExploreCardInteraction",
            data: {
                CardType: "SearchBar",
                InteractionType: "SearchBar",
            },
        });
        setIsActiveSearchBar(true);
    }, []);
    
    const handleSearchBarOnBlur = React.useCallback((e: React.FocusEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget) &&
            !isGlobalFeatureEnabled("msplaces-search-debug-css")) {
            setIsActiveSearchBar(false);
        }
    }, []);
    
    // Force selected tab to update if route changes from somewhere that is not a tab click
    // i.e. if navigates via search bar
    React.useEffect(() => {
        /* eslint-disable-next-line owa-custom-rules/forbid-react-set-state-and-mutators-inside-specific-hooks -- (https://aka.ms/OWALintWiki)
         * BASELINE. DO NOT COPY AND PASTE!, replace this line with a justification or fix the error.
         *	> React setState function 'setSelectedTab' cannot be called inside 'useEffect' to avoid unnecessary re-renders */
        setSelectedTab(defaultRoute);
    }, [defaultRoute]);
    
    const handleSuggestionClick = React.useCallback((suggestion: SearchSuggestion, _searchProviderKey: string, _alternativeAction: boolean) => {
        setIsActiveSearchBar(false);
        if (suggestion.href) {
            // If suggestion navigates force selected tab to update
            setSelectedTab(defaultRoute);
        }
    }, [defaultRoute]);
    
    const searchBar = React.useMemo(() => {
        return ((isActiveSearchBar || isScreenXLargeUp) && (<div className={isActiveSearchBar ? placesSearchBarActive : placesSearchBar} onBlur={handleSearchBarOnBlur} data-telemetry-id={"SearchBar"}>
                        <PlacesSearchBar onFocus={onSearchButtonClick} autoFocus={isActiveSearchBar} onSuggestionClicked={handleSuggestionClick} isMediumScreen={isScreenMediumDown}/>
                    </div>));
    }, [
        isActiveSearchBar,
        isScreenXLargeUp,
        handleSearchBarOnBlur,
        onSearchButtonClick,
        handleSuggestionClick,
        isScreenMediumDown,
    ]);
    
    // Set up F6 navigation for the header
    const headerNavContainerRef = React.useRef<HTMLDivElement>(null);
    useSectionNavigation(headerNavContainerRef, HybridspaceSectionNavigation.NavigationHeader);
    
    // If user is on the Management or Analytics page through url path, the role switcher should be visible
    const shouldShowRoleSwitcher = (isAdminEnabled || isAnalyticsHomePage || isSpaceManagementAdminPage) &&
        !roleSwitcherDisabled;
    
    const { roleSwitcherRef } = useRoleSwitcherLoadE2E(defaultRoute, routeToPageMap, shouldShowRoleSwitcher, isChecking, checkFailed);
    
    return (<div ref={headerNavContainerRef} className={mergeClasses(isStandaloneApp ? navHeader : navHeaderMetaOS, navHeaderUpNextRefresh)} data-telemetry-id="NavigationHeader" tabIndex={-1} // Allow F6 navigation
    >
                {!searchingOnSmallScreen && <HybridspaceLogo />}
                {showTabList && !searchingOnMediumScreen && (<AccTabList items={links} selectedKey={selectedTab} appearance={"subtle"} onSelectedKeyChange={handleSelectedKeyChange} responsiveTabs={true} isAppFolderOverflowIcon={true}/>)}

                {!isAnalyticsHomePage && !isSpaceManagementAdminPage && searchEnabled && (<>
                        {searchBar}
                        {!isActiveSearchBar && (<Tooltip content={loc(searchButtonAriaLabel)} relationship="label">
                                <Button className={searchButton} icon={searchIcon} appearance="subtle" onClick={onSearchButtonClick} data-telemetry-id={"SearchButton"} aria-label={loc(searchButtonAriaLabel)}></Button>
                            </Tooltip>)}
                    </>)}

                {shouldShowRoleSwitcher && (<HybridspaceRoleSwitcher ref={
            // TODO (456964): Remove this React 18 compatibility cast after adopting React 19 types.
            roleSwitcherRef as React.Ref<HTMLButtonElement>}/>)}

                <div className={settingsArea}>
                    {!searchingOnSmallScreen && (searchEnabled || shouldShowRoleSwitcher) && (<Divider vertical={true} className={divider}/>)}
                    <SmallScreenNav links={links} navigate={handleSelectedKeyChange} selectedRoute={selectedTab} isSmallScreen={isScreenSmallDown} isStandaloneApp={isStandaloneApp}/>
                    <PlacesMoreDropdown isStandaloneApp={isStandaloneApp}/>
                    <div className={mergeClasses(isStandaloneApp ? meControlPadding : metaOSPadding, meControlWhiteBackground)} data-telemetry-id="MeControl">
                        {isStandaloneApp && (<HybridspaceSuiteHeader compact={showCompactAppHeader}/>)}
                    </div>
                </div>
            </div>);
}, "HybridspaceNavHeader");
export default defaultExport;
